import { motion } from "framer-motion";
import { Mail, ArrowUpRight, Phone, MapPin } from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_life-architecture/artifacts/q5vdg061_Logo%20Final.png";

const MAP_LAT = 23.269246549164958;
const MAP_LNG = 72.30897203091368;
const MAP_EMBED = `https://www.google.com/maps?q=${MAP_LAT},${MAP_LNG}&hl=en&z=15&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${MAP_LAT},${MAP_LNG}`;

export const Footer = () => {
  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="relative bg-[#030704] border-t border-white/[0.06] overflow-hidden"
    >
      {/* Faint emerald glow */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#10B981]/60 to-transparent" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-96 w-[80%] rounded-full bg-[#10B981]/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 md:pt-28 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          {/* Left contact */}
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#10B981] mb-6 flex items-center gap-3">
              <span className="h-px w-6 bg-[#10B981]" />
              Get in touch
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight leading-snug">
              Discover the molecules
              <br />
              engineering tomorrow.
            </h3>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-start sm:gap-10 gap-5">
              <a
                href="mailto:info@colossusbioref.com"
                data-testid="footer-email-cta"
                className="group inline-flex items-center gap-3 border-b border-white/15 pb-3 text-base md:text-lg font-body text-white hover:border-[#10B981] transition-colors"
              >
                <Mail className="h-4 w-4 text-[#10B981]" />
                info@colossusbioref.com
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>

              <a
                href="tel:+919825063450"
                data-testid="footer-phone-cta"
                className="group inline-flex items-center gap-3 border-b border-white/15 pb-3 text-base md:text-lg font-body text-white hover:border-[#10B981] transition-colors"
              >
                <Phone className="h-4 w-4 text-[#10B981]" />
                +91 98250 63450
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>

            {/* Address + Map side-by-side */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-stretch">
              <div className="flex items-start gap-3" data-testid="footer-address">
                <MapPin className="h-5 w-5 text-[#10B981] mt-1 shrink-0" />
                <address className="not-italic font-body text-sm md:text-base text-white/75 leading-[1.7]">
                  <span className="block text-white font-medium">
                    Colossus Biorefinery Private Limited
                  </span>
                  27 &amp; 28, Labh Estate,
                  <br />
                  Taluka Narsihpura, Kadi,
                  <br />
                  Gujarat, India — 384440
                </address>
              </div>

              {/* Google Map */}
              <div className="relative rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.02] shadow-[0_25px_70px_-25px_rgba(16,185,129,0.4)] min-h-[220px]">
                <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-xl pointer-events-none z-10" />
                <iframe
                  title="Colossus Biorefinery — Kadi, Gujarat"
                  src={MAP_EMBED}
                  className="w-full h-full min-h-[220px] absolute inset-0"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="footer-map"
                />
                <a
                  href={MAP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-map-cta"
                  className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-2 rounded-full bg-[#030704]/85 backdrop-blur-md border border-[#10B981]/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-white hover:bg-[#10B981]/20 transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5 text-[#10B981]" />
                  Open
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            <p className="mt-8 text-xs uppercase tracking-[0.25em] text-white/40">
              Pharma · Food · Clinical Nutrition · Bio-actives
            </p>
          </div>

          {/* Right: meta */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
                Sectors
              </div>
              <ul className="space-y-2 text-sm text-white/70 font-body">
                <li>Pharmaceutical</li>
                <li>Food &amp; Beverage</li>
                <li>Clinical Nutrition</li>
                <li>Pediatric &amp; Geriatric</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
                Principles
              </div>
              <ul className="space-y-2 text-sm text-white/70 font-body">
                <li>100% Botanical</li>
                <li>Cruelty-free</li>
                <li>Closed-loop</li>
                <li>Trade-secret science</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Massive wordmark */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          className="select-none"
        >
          <div className="flex items-end gap-6 mb-8">
            <div className="relative h-24 w-24 md:h-28 md:w-28 rounded-xl overflow-hidden ring-1 ring-[#10B981]/30 shadow-[0_20px_60px_-15px_rgba(16,185,129,0.4)] bg-white">
              <img
                src={LOGO}
                alt="Colossus Biorefinery"
                className="h-full w-full object-contain"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-xl pointer-events-none" />
            </div>
            <div className="pb-1">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#10B981] mb-1">
                Colossus Biorefinery Pvt Ltd
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50">
                Made in India · Engineered for the world
              </div>
            </div>
          </div>
          <div
            className="font-brand text-white leading-none"
            style={{ fontSize: "clamp(72px, 18vw, 280px)" }}
            data-testid="footer-wordmark"
          >
            COLOSSUS<span className="text-[#10B981]">.</span>
          </div>
        </motion.div>

        {/* Bottom strip */}
        <div className="mt-12 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] uppercase tracking-[0.25em] text-white/40 font-body">
          <div>Proprietary Science · National Impact · Planet-Positive Future</div>
          <div className="flex items-center gap-6">
            <span>© {new Date().getFullYear()} Colossus Bio Refinery</span>
            <a href="#top" className="hover:text-white transition-colors">
              Back to top ↑
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
